import React, { memo, useCallback } from 'react'
import CheckFilledIcon from 'transfix-icons/build/CheckFilledIcon'
import RejectedFilledIcon from 'transfix-icons/build/RejectedFilledIcon'
import { Button } from 'transfix-ui/components/Button'
import { Typography, makeStyles } from 'transfix-ui/core'
import { IBidApproval, IBidApprovalStateEnum, IUser, Maybe } from 'types/graphqlTypes'
import { NotAvailableIcon, CheckOutlineIcon } from 'transfix-icons'
import { useModalState } from 'utils/hooks'
import ApproveOptionConfirmationModal from './ApproveOptionConfirmationModal'
import RejectOptionConfirmationModal from './RejectOptionConfirmationModal'
import { TEST_IDS } from './constants'

type TBidApprovalPartial = Pick<IBidApproval, 'id' | 'state'> & {
  updatedBy?: Maybe<Pick<IUser, 'id' | 'fullName'>>
}

export interface IOptionReviewButtonGroupProps {
  bidApproval: TBidApprovalPartial
  compact?: boolean
}

const useOptionReviewButtonGroupStyles = makeStyles(theme => ({
  buttons: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
  },
  icons: {
    padding: theme.spacing(0.5),
    minWidth: 'auto',
  },
  stateText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

const OptionReviewButtonGroup = memo<IOptionReviewButtonGroupProps>(
  ({ bidApproval, compact = false }) => {
    const classes = useOptionReviewButtonGroupStyles()

    const { id: bidApprovalId, state: approvalState, updatedBy } = bidApproval || {}
    const { fullName: approvedByName } = updatedBy || {}

    const isPending = approvalState === IBidApprovalStateEnum.open
    const isApproved = approvalState === IBidApprovalStateEnum.approved
    const isRejected = approvalState === IBidApprovalStateEnum.declined

    const { modalState, toggleModal } = useModalState({
      approveModal: false,
      rejectModal: false,
    })

    const createStopPropagationHandler = <T extends keyof typeof modalState>(
      modalName: T,
      isOpen: boolean
    ) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      toggleModal(modalName, isOpen)
    }

    const { approveModal: isApproveModalOpen, rejectModal: isRejectModalOpen } = modalState || {}
    const openApproveModalHandler = createStopPropagationHandler('approveModal', true)
    const openRejectModalHandler = createStopPropagationHandler('rejectModal', true)

    const closeModalHandler = useCallback(() => {
      if (isApproveModalOpen) {
        toggleModal('approveModal', false)
      } else if (isRejectModalOpen) {
        toggleModal('rejectModal', false)
      }
    }, [isApproveModalOpen, isRejectModalOpen, toggleModal])

    return (
      <>
        {isPending && compact && (
          <div className={classes.buttons}>
            <Button
              aria-label='Reject'
              color='secondary'
              onClick={openRejectModalHandler}
              data-test={TEST_IDS.rejectButton}
              className={classes.icons}>
              <NotAvailableIcon size='large' />
            </Button>
            <Button
              aria-label='Approve'
              color='secondary'
              onClick={openApproveModalHandler}
              data-test={TEST_IDS.approveButton}
              className={classes.icons}>
              <CheckOutlineIcon size='large' />
            </Button>
          </div>
        )}
        {isPending && !compact && (
          <div className={classes.buttons}>
            <Button
              color='secondary'
              onClick={openRejectModalHandler}
              data-test={TEST_IDS.rejectButton}
              fullWidth>
              Reject
            </Button>
            <Button
              color='primary'
              onClick={openApproveModalHandler}
              data-test={TEST_IDS.approveButton}
              fullWidth>
              Approve
            </Button>
          </div>
        )}
        {isApproved && !compact && (
          <div className={classes.stateText} data-test={TEST_IDS.approvedText}>
            <CheckFilledIcon color='green2' size='xlarge' />
            <Typography variant='subtitle1' component='div'>
              {approvedByName
                ? `${approvedByName} approved this option`
                : 'This option has been approved'}
            </Typography>
          </div>
        )}
        {isRejected && !compact && (
          <div className={classes.stateText} data-test={TEST_IDS.rejectedText}>
            <RejectedFilledIcon color='coolGray4' size='xlarge' />
            <Typography variant='subtitle1' component='div'>
              {approvedByName
                ? `${approvedByName} rejected this option`
                : 'This option has been rejected'}
            </Typography>
          </div>
        )}

        {isApproveModalOpen && (
          <ApproveOptionConfirmationModal
            bidApprovalId={bidApprovalId}
            onSuccess={closeModalHandler}
            onClose={closeModalHandler}
          />
        )}
        {isRejectModalOpen && (
          <RejectOptionConfirmationModal
            bidApprovalId={bidApprovalId}
            onSuccess={closeModalHandler}
            onClose={closeModalHandler}
          />
        )}
      </>
    )
  }
)
export default OptionReviewButtonGroup
